import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, Timestamp, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { CheckCircle, XCircle, Clock, Building2, Phone, Mail, MapPin, Search, Calendar, CreditCard, Eye } from 'lucide-react';

function AdminLabApproval({ user }) {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectingPayment, setRejectingPayment] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 });

  const loadPayments = useCallback(async () => {
    try {
      setLoading(true);
      
      // servicePayments에서 lab-advertisement 타입 가져오기
      let q;
      if (filter === 'all') {
        q = query(
          collection(db, 'servicePayments'),
          where('serviceType', '==', 'lab-advertisement'),
          orderBy('createdAt', 'desc')
        );
      } else {
        q = query(
          collection(db, 'servicePayments'),
          where('serviceType', '==', 'lab-advertisement'),
          where('status', '==', filter),
          orderBy('createdAt', 'desc')
        );
      }
      
      const snapshot = await getDocs(q);
      const paymentsList = await Promise.all(snapshot.docs.map(async (docSnap) => {
        const paymentData = docSnap.data();
        
        // 기공소 정보 가져오기
        let labData = null;
        if (paymentData.contentId) {
          const labDoc = await getDoc(doc(db, 'labAdvertisements', paymentData.contentId));
          if (labDoc.exists()) {
            labData = labDoc.data();
          }
        }
        
        // 사용자 정보 가져오기
        let userData = null;
        if (paymentData.userId) {
          const userDoc = await getDoc(doc(db, 'users', paymentData.userId));
          if (userDoc.exists()) {
            userData = userDoc.data();
          }
        }
        
        return {
          id: docSnap.id,
          ...paymentData,
          labData,
          userData
        };
      }));
      
      setPayments(paymentsList);
      
      // 통계 계산
      const allPayments = await getDocs(query(
        collection(db, 'servicePayments'),
        where('serviceType', '==', 'lab-advertisement')
      ));
      const allList = allPayments.docs.map(d => d.data());
      setStats({
        total: allList.length,
        pending: allList.filter(p => p.status === 'pending' || !p.status).length,
        approved: allList.filter(p => p.status === 'approved').length,
        rejected: allList.filter(p => p.status === 'rejected').length
      });
      
    } catch (error) {
      console.error('결제 내역 로딩 실패:', error);
      
      // 인덱스 오류시 fallback
      if (error.code === 'failed-precondition' || error.message.includes('index')) {
        try {
          const fallbackQ = query(
            collection(db, 'servicePayments'),
            where('serviceType', '==', 'lab-advertisement')
          );
          const fallbackSnapshot = await getDocs(fallbackQ);
          let paymentsList = fallbackSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          if (filter !== 'all') {
            paymentsList = paymentsList.filter(p => (p.status || 'pending') === filter);
          }
          
          paymentsList.sort((a, b) => (b.createdAt?.toMillis() || 0) - (a.createdAt?.toMillis() || 0));
          setPayments(paymentsList);
        } catch (fallbackError) {
          console.error('Fallback 로딩 실패:', fallbackError);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (user) loadPayments();
  }, [user, filter, loadPayments]);

  const handleApprove = async (payment) => {
    if (!window.confirm(`"${payment.labData?.businessName || payment.userName || '이 기공소'}" 홍보를 승인하시겠습니까?`)) return;
    
    try {
      setProcessing(true);
      
      // 결제 상태 업데이트
      await updateDoc(doc(db, 'servicePayments', payment.id), {
        status: 'approved',
        approvedBy: user.uid,
        approvedAt: Timestamp.now()
      });
      
      // 기공소 광고 상태도 업데이트
      if (payment.contentId) {
        await updateDoc(doc(db, 'labAdvertisements', payment.contentId), {
          status: 'approved',
          isActive: true,
          approvedBy: user.uid,
          approvedAt: Timestamp.now(),
          expiryDate: payment.expiryDate || Timestamp.fromDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000))
        });
      }
      
      alert('기공소 홍보 승인이 완료되었습니다!');
      loadPayments();
    } catch (error) {
      console.error('승인 처리 실패:', error);
      alert('승인 처리 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectClick = (payment) => {
    setRejectingPayment(payment);
    setRejectReason('');
    setShowRejectModal(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectReason.trim()) {
      alert('거부 사유를 입력해주세요.');
      return;
    }
    
    try {
      setProcessing(true);
      
      // 결제 상태 업데이트
      await updateDoc(doc(db, 'servicePayments', rejectingPayment.id), {
        status: 'rejected',
        rejectedBy: user.uid,
        rejectedAt: Timestamp.now(),
        rejectionReason: rejectReason
      });
      
      // 기공소 광고 상태도 업데이트
      if (rejectingPayment.contentId) {
        await updateDoc(doc(db, 'labAdvertisements', rejectingPayment.contentId), {
          status: 'rejected',
          isActive: false,
          rejectedBy: user.uid,
          rejectedAt: Timestamp.now(),
          rejectionReason: rejectReason
        });
      }
      
      alert('기공소 홍보 신청이 거부되었습니다.');
      setShowRejectModal(false);
      setRejectingPayment(null);
      setRejectReason('');
      loadPayments();
    } catch (error) {
      console.error('거부 처리 실패:', error);
      alert('거부 처리 중 오류가 발생했습니다: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      pending: { bg: '#fef3c7', color: '#92400e', icon: <Clock size={16} />, text: '승인 대기' },
      approved: { bg: '#d1fae5', color: '#065f46', icon: <CheckCircle size={16} />, text: '승인됨' },
      rejected: { bg: '#fee2e2', color: '#991b1b', icon: <XCircle size={16} />, text: '거부됨' }
    };
    const s = config[status] || config.pending;
    return (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', backgroundColor: s.bg, color: s.color, borderRadius: '8px', fontSize: '13px', fontWeight: '600' }}>
        {s.icon}{s.text}
      </div>
    );
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const filteredPayments = payments.filter(payment => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      payment.labData?.businessName?.toLowerCase().includes(query) ||
      payment.userName?.toLowerCase().includes(query) ||
      payment.userEmail?.toLowerCase().includes(query) ||
      payment.orderNumber?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <div style={styles.loading}>
        <div style={styles.spinner}></div>
        <p>로딩 중...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <Building2 size={32} color="#6366f1" />
            기공소 홍보 승인 관리
          </h1>
          <p style={styles.subtitle}>결제 완료된 기공소 홍보 신청을 검토하고 승인/거부하세요</p>
        </div>
      </div>

      {/* 통계 카드 */}
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#dbeafe', color: '#3b82f6' }}>
            <Building2 size={24} />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>{stats.total}</div>
            <div style={styles.statLabel}>전체 신청</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#fef3c7', color: '#f59e0b' }}>
            <Clock size={24} />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>{stats.pending}</div>
            <div style={styles.statLabel}>승인 대기</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#d1fae5', color: '#10b981' }}>
            <CheckCircle size={24} />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>{stats.approved}</div>
            <div style={styles.statLabel}>승인 완료</div>
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={{ ...styles.statIcon, backgroundColor: '#fee2e2', color: '#ef4444' }}>
            <XCircle size={24} />
          </div>
          <div style={styles.statContent}>
            <div style={styles.statValue}>{stats.rejected}</div>
            <div style={styles.statLabel}>거부됨</div>
          </div>
        </div>
      </div>

      {/* 컨트롤 */}
      <div style={styles.controls}>
        <div style={styles.filterButtons}>
          {[
            { key: 'all', label: '전체' },
            { key: 'pending', label: '대기중' },
            { key: 'approved', label: '승인됨' },
            { key: 'rejected', label: '거부됨' }
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={filter === f.key ? { ...styles.filterButton, ...styles.filterButtonActive } : styles.filterButton}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={styles.searchBox}>
          <Search size={20} color="#94a3b8" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="기공소명, 신청자, 주문번호 검색..."
            style={styles.searchInput}
          />
        </div>
      </div>

      {/* 목록 */}
      {filteredPayments.length === 0 ? (
        <div style={styles.emptyState}>
          <Building2 size={64} color="#cbd5e1" />
          <p style={styles.emptyText}>기공소 홍보 신청 내역이 없습니다</p>
        </div>
      ) : (
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>주문번호</th>
                <th style={styles.th}>기공소명</th>
                <th style={styles.th}>신청자</th>
                <th style={styles.th}>결제금액</th>
                <th style={styles.th}>기간</th>
                <th style={styles.th}>신청일</th>
                <th style={styles.th}>상태</th>
                <th style={styles.th}>관리</th>
              </tr>
            </thead>
            <tbody>
              {filteredPayments.map(payment => (
                <tr key={payment.id} style={styles.tr}>
                  <td style={styles.td}>
                    <span style={styles.orderNumber}>{payment.orderNumber || '-'}</span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.labInfo}>
                      <div style={styles.labIconSmall}><Building2 size={18} color="#6366f1" /></div>
                      <div>
                        <div style={styles.labName}>{payment.labData?.businessName || payment.businessName || '-'}</div>
                        <div style={styles.labAddress}>{payment.labData?.address || '-'}</div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.userInfo}>
                      <div style={styles.userName}>{payment.userName || '-'}</div>
                      <div style={styles.userEmail}>{payment.userEmail || '-'}</div>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <strong style={styles.amount}>{payment.amount?.toLocaleString() || 0}원</strong>
                  </td>
                  <td style={styles.td}>{payment.duration || 30}일</td>
                  <td style={styles.td}>{formatDate(payment.createdAt)}</td>
                  <td style={styles.td}>{getStatusBadge(payment.status || 'pending')}</td>
                  <td style={styles.td}>
                    <div style={styles.actionButtons}>
                      <button
                        onClick={() => { setSelectedPayment(payment); setShowDetailModal(true); }}
                        style={styles.actionButton}
                        title="상세보기"
                      >
                        <Eye size={18} />
                      </button>
                      {/* 승인 대기 상태일 때 승인/거부 버튼 표시 */}
                      {(payment.status === 'pending' || !payment.status) && (
                        <>
                          <button
                            onClick={() => handleApprove(payment)}
                            style={{ ...styles.actionButton, ...styles.approveActionBtn }}
                            disabled={processing}
                            title="승인"
                          >
                            <CheckCircle size={18} />
                          </button>
                          <button
                            onClick={() => handleRejectClick(payment)}
                            style={{ ...styles.actionButton, ...styles.rejectActionBtn }}
                            disabled={processing}
                            title="거부"
                          >
                            <XCircle size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 상세 모달 */}
      {showDetailModal && selectedPayment && (
        <div style={styles.modalOverlay} onClick={() => setShowDetailModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>신청 상세 정보</h2>
              <button onClick={() => setShowDetailModal(false)} style={styles.modalCloseButton}>×</button>
            </div>
            <div style={styles.modalBody}>
              {/* 결제 정보 */}
              <div style={styles.detailSection}>
                <h3 style={styles.detailSectionTitle}><CreditCard size={18} /> 결제 정보</h3>
                <div style={styles.detailGrid}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>주문번호</span>
                    <span style={styles.detailValue}>{selectedPayment.orderNumber || '-'}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>결제금액</span>
                    <span style={styles.detailValue}>{selectedPayment.amount?.toLocaleString()}원</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>기간</span>
                    <span style={styles.detailValue}>{selectedPayment.duration || 30}일</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>상태</span>
                    {getStatusBadge(selectedPayment.status || 'pending')}
                  </div>
                </div>
              </div>

              {/* 기공소 정보 */}
              <div style={styles.detailSection}>
                <h3 style={styles.detailSectionTitle}><Building2 size={18} /> 기공소 정보</h3>
                <div style={styles.detailGrid}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>기공소명</span>
                    <span style={styles.detailValue}>{selectedPayment.labData?.businessName || '-'}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>주소</span>
                    <span style={styles.detailValue}>{selectedPayment.labData?.address || '-'}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>연락처</span>
                    <span style={styles.detailValue}>{selectedPayment.labData?.phone || '-'}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>이메일</span>
                    <span style={styles.detailValue}>{selectedPayment.labData?.email || '-'}</span>
                  </div>
                </div>
                {selectedPayment.labData?.introduction && (
                  <div style={styles.introBox}>
                    <strong>소개:</strong>
                    <p>{selectedPayment.labData.introduction}</p>
                  </div>
                )}
              </div>

              {/* 신청자 정보 */}
              <div style={styles.detailSection}>
                <h3 style={styles.detailSectionTitle}><Mail size={18} /> 신청자 정보</h3>
                <div style={styles.detailGrid}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>이름</span>
                    <span style={styles.detailValue}>{selectedPayment.userName || '-'}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>이메일</span>
                    <span style={styles.detailValue}>{selectedPayment.userEmail || '-'}</span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>연락처</span>
                    <span style={styles.detailValue}>{selectedPayment.userPhone || '-'}</span>
                  </div>
                </div>
              </div>

              {/* 거부 사유 */}
              {selectedPayment.status === 'rejected' && selectedPayment.rejectionReason && (
                <div style={styles.detailSection}>
                  <h3 style={styles.detailSectionTitle}><XCircle size={18} /> 거부 정보</h3>
                  <div style={styles.rejectionBox}>{selectedPayment.rejectionReason}</div>
                </div>
              )}
            </div>
            <div style={styles.modalFooter}>
              {(selectedPayment.status === 'pending' || !selectedPayment.status) && (
                <>
                  <button
                    onClick={() => { setShowDetailModal(false); handleApprove(selectedPayment); }}
                    style={styles.approveButtonLarge}
                    disabled={processing}
                  >
                    <CheckCircle size={20} /> 승인
                  </button>
                  <button
                    onClick={() => { setShowDetailModal(false); handleRejectClick(selectedPayment); }}
                    style={styles.rejectButtonLarge}
                    disabled={processing}
                  >
                    <XCircle size={20} /> 거부
                  </button>
                </>
              )}
              <button onClick={() => setShowDetailModal(false)} style={styles.closeButtonLarge}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 거부 사유 모달 */}
      {showRejectModal && (
        <div style={styles.modalOverlay} onClick={() => setShowRejectModal(false)}>
          <div style={styles.rejectModal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>거부 사유 입력</h3>
            <textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="거부 사유를 입력해주세요..."
              style={styles.rejectTextarea}
              rows={5}
            />
            <div style={styles.rejectModalButtons}>
              <button
                onClick={handleRejectSubmit}
                style={styles.rejectSubmitButton}
                disabled={processing || !rejectReason.trim()}
              >
                거부 확정
              </button>
              <button
                onClick={() => setShowRejectModal(false)}
                style={styles.rejectCancelButton}
              >
                취소
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        table tr:hover { background-color: #f8fafc; }
        button:hover:not(:disabled) { opacity: 0.85; }
        button:disabled { opacity: 0.5; cursor: not-allowed; }
      `}</style>
    </div>
  );
}

const styles = {
  container: { maxWidth: '1400px', margin: '0 auto', padding: '24px' },
  header: { marginBottom: '24px' },
  title: { display: 'flex', alignItems: 'center', gap: '12px', fontSize: '28px', fontWeight: '700', color: '#0f172a', margin: 0 },
  subtitle: { fontSize: '14px', color: '#64748b', margin: '8px 0 0 0' },
  
  // 통계
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '24px' },
  statCard: { display: 'flex', alignItems: 'center', gap: '16px', padding: '20px', backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0' },
  statIcon: { width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '12px' },
  statContent: { flex: 1 },
  statValue: { fontSize: '24px', fontWeight: '700', color: '#0f172a' },
  statLabel: { fontSize: '13px', color: '#64748b' },
  
  // 컨트롤
  controls: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' },
  filterButtons: { display: 'flex', gap: '8px' },
  filterButton: { padding: '10px 20px', backgroundColor: '#f8fafc', color: '#64748b', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' },
  filterButtonActive: { backgroundColor: '#6366f1', color: 'white', borderColor: '#6366f1' },
  searchBox: { display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 16px', backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '10px', minWidth: '300px' },
  searchInput: { flex: 1, border: 'none', outline: 'none', fontSize: '14px' },
  
  // 테이블
  tableContainer: { backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { padding: '16px', textAlign: 'left', fontSize: '13px', fontWeight: '700', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e2e8f0', backgroundColor: '#f8fafc' },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s' },
  td: { padding: '16px', fontSize: '14px', color: '#0f172a' },
  
  orderNumber: { fontFamily: 'monospace', fontSize: '13px', color: '#6366f1', fontWeight: '600' },
  labInfo: { display: 'flex', alignItems: 'center', gap: '12px' },
  labIconSmall: { width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#eef2ff', borderRadius: '8px', flexShrink: 0 },
  labName: { fontWeight: '600', color: '#0f172a' },
  labAddress: { fontSize: '12px', color: '#64748b', marginTop: '2px' },
  userInfo: { display: 'flex', flexDirection: 'column', gap: '2px' },
  userName: { fontWeight: '600', color: '#0f172a' },
  userEmail: { fontSize: '12px', color: '#64748b' },
  amount: { color: '#6366f1', fontWeight: '700' },
  
  // 액션 버튼
  actionButtons: { display: 'flex', gap: '8px' },
  actionButton: { width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #e2e8f0', borderRadius: '8px', backgroundColor: 'white', color: '#64748b', cursor: 'pointer', transition: 'all 0.2s' },
  approveActionBtn: { borderColor: '#10b981', color: '#10b981' },
  rejectActionBtn: { borderColor: '#ef4444', color: '#ef4444' },
  
  // 빈 상태
  emptyState: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px', textAlign: 'center', backgroundColor: 'white', borderRadius: '12px' },
  emptyText: { fontSize: '18px', fontWeight: '600', color: '#64748b', margin: '16px 0 0 0' },
  
  // 로딩
  loading: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '80px 20px' },
  spinner: { width: '48px', height: '48px', border: '4px solid #e2e8f0', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' },
  
  // 모달
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modalContent: { backgroundColor: 'white', borderRadius: '16px', maxWidth: '700px', width: '100%', maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '24px', borderBottom: '1px solid #e2e8f0' },
  modalTitle: { margin: 0, fontSize: '20px', fontWeight: '700', color: '#0f172a' },
  modalCloseButton: { width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', backgroundColor: 'transparent', fontSize: '24px', color: '#64748b', cursor: 'pointer', borderRadius: '6px' },
  modalBody: { padding: '24px' },
  modalFooter: { display: 'flex', gap: '12px', padding: '24px', borderTop: '1px solid #e2e8f0', justifyContent: 'flex-end' },
  
  detailSection: { marginBottom: '24px' },
  detailSectionTitle: { display: 'flex', alignItems: 'center', gap: '8px', margin: '0 0 16px 0', fontSize: '16px', fontWeight: '700', color: '#0f172a' },
  detailGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' },
  detailItem: { display: 'flex', flexDirection: 'column', gap: '4px', padding: '12px', backgroundColor: '#f8fafc', borderRadius: '8px' },
  detailLabel: { fontSize: '12px', color: '#64748b', fontWeight: '600' },
  detailValue: { fontSize: '14px', color: '#0f172a', fontWeight: '500' },
  introBox: { padding: '16px', backgroundColor: '#f8fafc', borderRadius: '8px', marginTop: '12px', fontSize: '14px', lineHeight: '1.6' },
  rejectionBox: { padding: '16px', backgroundColor: '#fee2e2', borderRadius: '8px', color: '#991b1b', fontSize: '14px' },
  
  approveButtonLarge: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  rejectButtonLarge: { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 24px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  closeButtonLarge: { padding: '12px 24px', backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  
  // 거부 모달
  rejectModal: { backgroundColor: 'white', borderRadius: '16px', padding: '24px', maxWidth: '500px', width: '100%' },
  rejectTextarea: { width: '100%', padding: '12px', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '15px', fontFamily: 'inherit', resize: 'vertical', outline: 'none', marginTop: '16px', marginBottom: '16px', boxSizing: 'border-box' },
  rejectModalButtons: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  rejectSubmitButton: { padding: '12px 24px', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  rejectCancelButton: { padding: '12px 24px', backgroundColor: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
};

export default AdminLabApproval;